import { DonutChart as FluentDonutChart, type ChartProps, type ChartDataPoint } from '@fluentui/react-charts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { seriesColor } from './colors';
import { formatValue } from './format';
import type { ChartUnit, Slice } from './types';

interface DonutChartProps {
  slices: readonly Slice[];
  /**
   * Inner radius (px) of the donut hole. Larger values produce a thinner
   * ring. Use `valueInsideDonut` to render text inside that hole.
   */
  innerRadius?: number;
  height?: number;
  width?: number;
  unit?: ChartUnit;
  hideLegend?: boolean;
  /** Optional accessible chart title. */
  chartTitle?: string;
  /** Render text in the donut hole (e.g. KPI total). */
  valueInsideDonut?: number | string;
}

const useStyles = makeStyles({
  wrapper: {
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Donut/ring chart for status-distribution and KPI breakdowns. For a 4-state
 * "healthy / degraded / unhealthy / unknown" rollup, supply the slice colors
 * via `slice.color` (use `statusPalette.*` from theme/tokens).
 */
export function DonutChart({
  slices,
  innerRadius = 55,
  height = 220,
  width = 220,
  unit,
  hideLegend,
  chartTitle,
  valueInsideDonut,
}: DonutChartProps) {
  const classes = useStyles();

  const data: ChartProps = useMemo(() => {
    const chartData: ChartDataPoint[] = slices.map((s, i) => ({
      legend: s.label,
      data: s.value,
      color: s.color ?? seriesColor(i),
      yAxisCalloutData: formatValue(s.value, unit),
    }));
    return { chartTitle, chartData };
  }, [slices, unit, chartTitle]);

  const insideText =
    valueInsideDonut === undefined
      ? undefined
      : typeof valueInsideDonut === 'number'
        ? formatValue(valueInsideDonut, unit)
        : valueInsideDonut;

  return (
    <div className={classes.wrapper}>
      <FluentDonutChart
        data={data}
        innerRadius={innerRadius}
        height={height}
        width={width}
        valueInsideDonut={insideText}
        hideLegend={hideLegend}
        reflowProps={{ mode: 'min-width' }}
      />
    </div>
  );
}
